/**
 * CreateAccountStage — SIGNUP-2: the three account-creation screens
 * from the redesign, rendered inside Auth's own state and handlers.
 *
 *   invited   — off the waitlist: email locked to the invite
 *   team      — joining a teammate's studio: no business name, the
 *               button names the studio
 *   invalid   — an invite that was used or expired: one line, one way
 *               to ask for another
 *
 * The stage is the prototype's: Apple/Google chips float above "or
 * with your email", the fields are lit capsules, the terms line sits
 * under the title, and Continue gathers the chips into the capsule
 * before the next screen (stageMotion.ts). Everything that submits,
 * validates or redirects is still Auth.tsx.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { AuthCollageBackground } from './AuthCollageBackground';
import { FirstRunFrame, StageCapsule, StageChip, StageCTA, StageHint, StageRow, StageText } from '@/components/firstrun/Stage';
import { enterStage } from '@/components/firstrun/stageMotion';
import { IC } from '@/components/firstrun/tokens';

const ACCENT = '#E8B54A';

export interface CreateAccountStageProps {
  variant: 'invited' | 'team' | 'invalid' | 'open';
  /** Studio name for the team variant; the business field otherwise. */
  studioName?: string;
  fullName: string;
  setFullName: (v: string) => void;
  orgName: string;
  setOrgName: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  emailLocked: boolean;
  password: string;
  setPassword: (v: string) => void;
  acceptedTerms: boolean;
  setAcceptedTerms: (v: boolean) => void;
  errors: Record<string, string>;
  isLoading: boolean;
  onSubmit: () => void;
  onApple: () => void;
  onGoogle: () => void;
  oauthEnabled: boolean;
  onBack: () => void;
  /** Invalid invite: where "ask for a new one" goes. */
  onRequestInvite: () => void;
  invalidMessage: string;
  footer?: ReactNode;
}

export function CreateAccountStage(p: CreateAccountStageProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    enterStage(rootRef.current, 1);
  }, [p.variant]);

  const firstError = Object.values(p.errors)[0];
  const first = p.fullName.trim().split(/\s+/)[0];

  const behind = (
    <div className="absolute inset-0 opacity-[.32]" style={{ maskImage: 'linear-gradient(180deg,#000 0%,rgba(0,0,0,.7) 45%,transparent 78%)', WebkitMaskImage: 'linear-gradient(180deg,#000 0%,rgba(0,0,0,.7) 45%,transparent 78%)' }} aria-hidden>
      <AuthCollageBackground scrim="transparent" />
    </div>
  );

  if (p.variant === 'invalid') {
    return (
      <FirstRunFrame
        rootRef={rootRef}
        palette="auth"
        accent={ACCENT}
        behind={behind}
        onBack={p.onBack}
        stage={
          <StageCapsule value={typeof window !== 'undefined' ? window.location.host + '/auth?invite=…' : 'orbit/auth?invite=…'} nova="curious" tag="Used" dim strike focus accent={ACCENT} />
        }
        text={
          <StageText count="Invite link" icon={IC.lock} accent={ACCENT} title="This invite can't be used." body={p.invalidMessage} />
        }
        cta={<StageCTA primary="Ask for a new invite" onPrimary={p.onRequestInvite} note="We'll email a fresh link. They're good for one account each." />}
      />
    );
  }

  const team = p.variant === 'team';
  const open = p.variant === 'open';
  const title = team
    ? `You're invited to ${p.studioName || 'the team'}.`
    : open
      ? 'Every business starts here.'
      : first
        ? `You're in, ${first}.`
        : "You're in.";
  const body = team
    ? "You'll share projects, clients and the calendar with the team."
    : open
      ? // OPEN-1 — every plan starts with the 7-day trial (card at the plan step).
        "A few details and you're in. Every plan starts with a 7-day free trial."
      : 'Your spot from the waitlist is ready. Create your account — every plan starts with a 7-day free trial.';
  const count = team ? 'Team invite' : open ? 'New account' : 'Early access';

  return (
    <FirstRunFrame
      rootRef={rootRef}
      palette="auth"
      accent={ACCENT}
      behind={behind}
      onBack={p.onBack}
      stage={
        <>
          {team && (
            <div data-a="block" className="flex w-full flex-col items-center">
              <div className="flex items-center gap-3 rounded-full bg-white/[0.14] py-2 pl-2 pr-[18px] backdrop-blur-xl" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,.2)' }}>
                <span className="grid h-9 w-9 place-items-center rounded-full font-bold text-[#1A1422]" style={{ background: '#7FB2E8' }}>
                  {(p.studioName || 'S').slice(0, 1).toUpperCase()}
                </span>
                <span className="text-[15px] font-semibold">{p.studioName || 'Your team'}</span>
                <span className="text-[13px] text-[#F2EFEB]/70">Team invite</span>
              </div>
            </div>
          )}
          {p.oauthEnabled && (
            <>
              <StageRow>
                <StageChip label="Continue with Apple" apple tall ink="#1A1422" onClick={p.onApple} index={0} accent={ACCENT} />
                <StageChip label="Continue with Google" google tall ink="#1A1422" onClick={p.onGoogle} index={1} accent={ACCENT} />
              </StageRow>
              <StageHint>or with your email</StageHint>
            </>
          )}
          <StageCapsule label="Name" value={p.fullName} editable onChange={p.setFullName} placeholder="Your name" autoComplete="name" focus accent={ACCENT} />
          {!team && (
            <StageCapsule label="Business" value={p.orgName} editable onChange={p.setOrgName} placeholder="Your business name" autoComplete="organization" accent={ACCENT} />
          )}
          <StageCapsule label="Email" value={p.email} editable={!p.emailLocked} onChange={p.setEmail} placeholder="you@example.com" inputType="email" autoComplete="email" locked={p.emailLocked} accent={ACCENT} />
          <StageCapsule label="Password" value={p.password} editable onChange={p.setPassword} placeholder="At least 8 characters" inputType="password" autoComplete="new-password" accent={ACCENT} />
          {firstError && (
            <StageHint>
              <span role="alert" className="text-red-300/90">{firstError}</span>
            </StageHint>
          )}
        </>
      }
      text={
        <StageText count={count} icon={team ? IC.users : IC.eye} accent={ACCENT} title={title} body={body}>
          <button
            type="button"
            data-a="text"
            onClick={() => p.setAcceptedTerms(!p.acceptedTerms)}
            className="mt-1 flex items-center gap-2.5 border-0 bg-transparent py-2 text-[14px] text-[#F2EFEB]/80"
            aria-pressed={p.acceptedTerms}
          >
            <span
              className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-[7px] text-[#1A1422]"
              style={{ background: p.acceptedTerms ? ACCENT : 'rgba(255,255,255,.14)', boxShadow: p.acceptedTerms ? 'none' : 'inset 0 0 0 1px rgba(255,255,255,.35)' }}
            >
              {p.acceptedTerms && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                  <path d="M2.5 6.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                </svg>
              )}
            </span>
            <span>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noreferrer" className="text-[#F2EFEB] underline-offset-4 hover:underline" onClick={(e) => e.stopPropagation()}>Terms</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#F2EFEB] underline-offset-4 hover:underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</a>
            </span>
          </button>
          {p.footer}
        </StageText>
      }
      cta={
        <StageCTA
          primary={team ? `Join ${p.studioName || 'the team'}` : 'Create your account'}
          onPrimary={p.onSubmit}
          loading={p.isLoading}
        />
      }
    />
  );
}

export default CreateAccountStage;
